import React, { useState, useEffect, useMemo } from 'react'
import { Card, Table, InputNumber, Input, message, Typography, Button, Space, Spin, Tag, Descriptions, Alert } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ArrowLeftOutlined, CheckOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { operatorApiFetch } from '@services/operator/apiService'
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext'
import useSmartBack from '@hooks/shared/useSmartBack'
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler'
import { pickingListService, type PickingListDetail } from '@services/operator/pickingListService'
import { formatDateTime } from '@/utils/dateTime'

const { Text } = Typography

// 儲位輸入框的即時驗證狀態
type ShelfValidationState = 'validating' | 'valid' | 'invalid'
// antd Input / InputNumber 的 status 取值（'' = 無狀態）
type FieldStatus = '' | 'warning' | 'error'

// 表格 flat row：一筆 ShelfAllocation（或操作員新增的待補儲位列）
interface ConfirmRow {
  key: string
  shelf_allocation_id: string | null
  picking_list_item_id: string
  sku?: string
  product_name?: string
  shelf_location: string
  original_shelf_location: string | null
  suggested_quantity: number
  actual_quantity: number
  is_new: boolean
}

const PickingListConfirm: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const goBack = useSmartBack()
  const { addJob } = useOperatorNotification()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [pickingList, setPickingList] = useState<PickingListDetail | null>(null)
  const [rows, setRows] = useState<ConfirmRow[]>([])
  // 每個 picking_list_item 的需求數量 { [pli_id]: quantity }
  const [requiredMap, setRequiredMap] = useState<Record<string, number>>({})
  // 每個 picking_list_item 已撿到 tote 的數量 { [pli_id]: picked_quantity }
  // reopen 後 ShelfSuggester 只重生 remaining=quantity-picked 的 SA；shortage 判定
  // 必須把已在 tote 的 picked 算進 actual，否則整批會被誤判成「N 個品項短收」。
  const [pickedMap, setPickedMap] = useState<Record<string, number>>({})
  // 儲位驗證狀態 { [rowKey]: 'validating' | 'valid' | 'invalid' }
  const [shelfValidation, setShelfValidation] = useState<Record<string, ShelfValidationState>>({})

  const fetchData = async () => {
    if (!id) return
    try {
      setLoading(true)
      const result = await pickingListService.getById(id, { withShelfAllocations: true })

      if (result.success) {
        setPickingList(result.data)

        // 建立需求 / 已撿 / pli 資訊三組 map
        const reqMap: Record<string, number> = {}
        const pickedM: Record<string, number> = {}
        const pliInfoMap: Record<string, { sku?: string; product_name?: string }> = {}
        result.data.picking_list_items.forEach(pli => {
          reqMap[pli.id] = pli.quantity
          pickedM[pli.id] = pli.picked_quantity ?? 0
          pliInfoMap[pli.id] = { sku: pli.sku ?? undefined, product_name: pli.product_name ?? undefined }
        })
        setRequiredMap(reqMap)
        setPickedMap(pickedM)

        // 建立 flat rows，按儲位排序（與 PDF 相同）
        const flatRows: ConfirmRow[] = (result.data.shelf_allocations ?? [])
          .map(sa => ({
            key: sa.id,
            shelf_allocation_id: sa.id,
            picking_list_item_id: sa.picking_list_item_id,
            sku: pliInfoMap[sa.picking_list_item_id]?.sku,
            product_name: pliInfoMap[sa.picking_list_item_id]?.product_name,
            shelf_location: sa.shelf_location ?? '',
            original_shelf_location: sa.shelf_location ?? null,
            suggested_quantity: sa.quantity,
            actual_quantity: sa.quantity,
            is_new: false
          }))
          .sort((a, b) => (a.shelf_location || '').localeCompare(b.shelf_location || ''))

        setRows(flatRows)
      } else {
        // 後端失敗只回 error code（見 BACKEND.md §6）；交給 handleApiError 走 common.error.<code>
        // 翻譯、查不到才 fallback。success:false（2xx/422）走這裡；其餘非 2xx 由 operatorApiFetch
        // throw → 下面 catch。
        handleApiError(result, { t, fallbackMessage: t('common.error.operation_failed') })
      }
    } catch (error) {
      // operatorApiFetch 對非 2xx/422 throw 並帶 errorCode；handleNetworkError 偵測到 errorCode
      // 會自動路由到 handleApiError（翻 common.error.<code>），純網路錯誤才顯示通用訊息。
      // utility 內部已 console.error，不在此重複 log。
      handleNetworkError(error, { t, fallbackMessage: t('common.error.operation_failed') })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) fetchData()
  }, [id])

  // --- Handlers ---

  const handleQuantityChange = (key: string, val: number | null) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, actual_quantity: val || 0 } : r))
  }

  const handleShelfChange = (key: string, val: string) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, shelf_location: val } : r))
    // 清除該行的驗證狀態，等 onBlur 再驗證
    setShelfValidation(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const validateShelfLocation = async (key: string, location: string) => {
    if (!location?.trim()) return
    setShelfValidation(prev => ({ ...prev, [key]: 'validating' }))
    try {
      const res = await operatorApiFetch(`shelves/validate_location.json?location=${encodeURIComponent(location.trim())}`)
      const result = await res.json()
      setShelfValidation(prev => ({ ...prev, [key]: result.data?.exists ? 'valid' : 'invalid' }))
    } catch {
      setShelfValidation(prev => ({ ...prev, [key]: 'invalid' }))
    }
  }

  const handleAddShelf = (afterKey: string, pickingListItemId: string, sku?: string, productName?: string) => {
    setRows(prev => {
      const idx = prev.findIndex(r => r.key === afterKey)
      const newRow: ConfirmRow = {
        key: `new_${Date.now()}_${Math.random()}`,
        shelf_allocation_id: null,
        picking_list_item_id: pickingListItemId,
        sku,
        product_name: productName,
        shelf_location: '',
        original_shelf_location: null,
        suggested_quantity: 0,
        actual_quantity: 0,
        is_new: true
      }
      const next = [...prev]
      next.splice(idx + 1, 0, newRow)
      return next
    })
  }

  const handleRemoveShelf = (key: string) => {
    setRows(prev => prev.filter(r => r.key !== key))
  }

  // --- Validation ---

  // 按 picking_list_item 計算實際合計
  const itemTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    rows.forEach(r => {
      if (!totals[r.picking_list_item_id]) totals[r.picking_list_item_id] = 0
      totals[r.picking_list_item_id] += (r.actual_quantity || 0)
    })
    return totals
  }, [rows])

  const getItemStatus = (pliId: string): 'exceed' | 'shortage' | 'ok' => {
    // actual = 本輪 SA 表單填的量 + 之前已撿到 tote 的量（reopen 場景必須相加，
    // 否則重生 SA 為空時會把已在 tote 的部分當成短收）。
    const actual = (itemTotals[pliId] || 0) + (pickedMap[pliId] || 0)
    const required = requiredMap[pliId] || 0
    if (actual > required) return 'exceed'
    if (actual < required) return 'shortage'
    return 'ok'
  }

  const exceedItems = Object.keys(requiredMap).filter(id => getItemStatus(id) === 'exceed')
  const shortageItems = Object.keys(requiredMap).filter(id => getItemStatus(id) === 'shortage')
  const hasEmptyLocations = rows.some(r => r.is_new && !r.shelf_location?.trim())
  const hasInvalidShelves = Object.values(shelfValidation).some(v => v === 'invalid')
  const hasValidatingShelves = Object.values(shelfValidation).some(v => v === 'validating')

  // --- Submit ---

  const handleConfirm = async () => {
    if (exceedItems.length > 0) {
      message.error(t('shared.picking_lists.confirm.exceed_error'))
      return
    }

    if (!id) return

    setSubmitting(true)
    try {
      const exceptions = rows
        .filter(r => {
          if (!r.shelf_allocation_id && r.actual_quantity === 0) return false
          return true
        })
        .map(r => ({
          item_id: r.picking_list_item_id,
          shelf_allocation_id: r.shelf_allocation_id,
          quantity: r.actual_quantity,
          new_shelf_location: r.is_new
            ? r.shelf_location
            : (r.shelf_location !== r.original_shelf_location ? r.shelf_location : undefined)
        }))

      const result = await pickingListService.confirm(id, exceptions)
      if (result.success) {
        // 波次最後一張 PL 完成時後端會自動觸發批次取號 worker；把 jid 註冊到背景任務列表，
        // 讓 operator 看得到進度（沒 jid 代表這張不是最後一張，或波次沒 fulfillment）。
        if (result.data?.batch_tracking_job_id) {
          addJob(
            result.data.batch_tracking_job_id,
            'background_job.workers.picking_waves.batch_request_tracking_numbers.title',
            { nameParams: { wave_name: result.data.picking_wave_name } }
          )
        }
        message.success(t('common.messages.update_success'))
        navigate(`/picking_lists/${id}`)
      } else {
        handleApiError(result, { t, fallbackMessage: t('common.error.operation_failed') })
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.operation_failed') })
    } finally {
      setSubmitting(false)
    }
  }

  // --- Columns ---

  const columns: ColumnsType<ConfirmRow> = [
    {
      title: t('models.shelf.location'),
      key: 'shelf_location',
      width: 160,
      render: (_, record) => {
        const validation = shelfValidation[record.key]
        const getShelfInputStatus = (): FieldStatus => {
          if (validation === 'invalid') return 'error'
          if (record.is_new && !record.shelf_location?.trim()) return 'error'
          if (!record.is_new && record.shelf_location !== record.original_shelf_location) return validation === 'valid' ? '' : 'warning'
          return ''
        }
        const needsValidation = record.is_new
          ? record.shelf_location?.trim()
          : record.shelf_location !== record.original_shelf_location && record.shelf_location?.trim()

        return record.is_new ? (
          <>
            <Input
              value={record.shelf_location}
              onChange={(e) => handleShelfChange(record.key, e.target.value)}
              onBlur={() => needsValidation && validateShelfLocation(record.key, record.shelf_location)}
              placeholder={t('shared.picking_lists.confirm.shelf_location_placeholder')}
              status={getShelfInputStatus()}
            />
            {validation === 'invalid' && (
              <Text type="danger" style={{ fontSize: 12 }}>{t('shared.picking_lists.confirm.shelf_not_found')}</Text>
            )}
          </>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('common.original')}: {record.original_shelf_location}
            </Text>
            <Input
              value={record.shelf_location}
              onChange={(e) => handleShelfChange(record.key, e.target.value)}
              onBlur={() => needsValidation && validateShelfLocation(record.key, record.shelf_location)}
              status={getShelfInputStatus()}
              placeholder={record.original_shelf_location ?? undefined}
            />
            {validation === 'invalid' && (
              <Text type="danger" style={{ fontSize: 12 }}>{t('shared.picking_lists.confirm.shelf_not_found')}</Text>
            )}
          </Space>
        )
      }
    },
    {
      title: t('models.product.sku'),
      key: 'sku',
      width: 120,
      render: (_, record) => {
        const status = getItemStatus(record.picking_list_item_id)
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{record.sku}</Text>
            {status === 'shortage' && (
              <Tag color="warning" style={{ fontSize: 10 }}>
                {t('shared.picking_lists.confirm.shortage_warning', {
                  diff: requiredMap[record.picking_list_item_id]
                        - (itemTotals[record.picking_list_item_id] || 0)
                        - (pickedMap[record.picking_list_item_id] || 0)
                })}
              </Tag>
            )}
            {status === 'exceed' && (
              <Tag color="error" style={{ fontSize: 10 }}>
                {t('shared.picking_lists.confirm.exceed_error')}
              </Tag>
            )}
          </Space>
        )
      }
    },
    {
      title: t('models.product.name'),
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true
    },
    {
      title: t('shared.picking_lists.confirm.suggested_qty'),
      key: 'suggested_quantity',
      align: 'right',
      width: 100,
      render: (_, record) => record.is_new
        ? <Tag color="green">{t('shared.picking_lists.confirm.new_shelf_tag')}</Tag>
        : record.suggested_quantity
    },
    {
      title: t('shared.picking_lists.confirm.actual_qty'),
      key: 'actual_quantity',
      width: 130,
      align: 'right',
      render: (_, record) => (
        <InputNumber
          min={0}
          value={record.actual_quantity}
          onChange={(val) => handleQuantityChange(record.key, val)}
          status={record.actual_quantity !== record.suggested_quantity ? 'warning' : ''}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleAddShelf(record.key, record.picking_list_item_id, record.sku, record.product_name)}
            title={t('shared.picking_lists.confirm.add_shelf')}
          />
          {record.is_new && (
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveShelf(record.key)}
            />
          )}
        </Space>
      )
    }
  ]

  // --- Render ---

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
  }

  if (!pickingList) {
    return <div>{t('shared.picking_lists.confirm.not_found')}</div>
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <Card
        title={
          <Space>
            {t('shared.picking_lists.confirm.page_title')}
            <Tag>{pickingList.display_name}</Tag>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Descriptions bordered size="small" column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
          <Descriptions.Item label={t('models.picking_list.name')}>
            {pickingList.display_name}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.created_at')}>
            {formatDateTime(pickingList.created_at)}
          </Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            {t('shared.picking_lists.confirm.help_text')}
          </Text>
        </div>
      </Card>

      {/* Flat table sorted by shelf location (same as PDF) */}
      <Card>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="key"
          pagination={false}
          size="middle"
          bordered
        />
      </Card>

      {/* Sticky Footer */}
      <div style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(8px)',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid #f0f0f0',
        marginTop: 16
      }}>
        <Space>
          {shortageItems.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('shared.picking_lists.confirm.shortage_summary', { count: shortageItems.length })}
              style={{ padding: '4px 12px' }}
            />
          )}
          {exceedItems.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={t('shared.picking_lists.confirm.exceed_summary')}
              style={{ padding: '4px 12px' }}
            />
          )}
        </Space>
        <Space>
          <Button onClick={goBack} size="large">
            {t('common.button.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={submitting}
            size="large"
            icon={<CheckOutlined />}
            disabled={exceedItems.length > 0 || hasEmptyLocations || hasInvalidShelves || hasValidatingShelves}
          >
            {t('shared.picking_lists.confirm.confirm_button')}
          </Button>
        </Space>
      </div>
    </div>
  )
}

export default PickingListConfirm
